import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "primereact/button";
import { TabView, TabPanel } from "primereact/tabview";
import DashboardLayout from "../../layouts/DashboardLayout";
import Loading from "../../../../components/Loading/Loading";
import WalletService from "../../../../service/WalletService";
import BankAccountService from "../../../../service/BankAccountService";
import useDashboardData from "../../hooks/useDashboardData";
import useCurrency from "../../../../hooks/useCurrency";
import { formatPrice } from "../../../../utilities/formatPrice";
import localeText from "../../../../locale";
import "./WalletPage.css";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import BankAccountsTab from "./BankAccounts/BankAccountsTab";
import FinancialTransactionsTable from "./components/FinancialTransactionsTable/FinancialTransactionsTable";
import LifeTimeWalletTable from "./components/LifeTimeWalletTable/LifeTimeWalletTable";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import { typeTransfer } from "../../components/WithdrawIntent/WithdrawIntent";
import SecurityValidationModal from "./components/SecurityValidationModal/SecurityValidationModal";

const WITHDRAW_OTP_LENGTH = 6;
const WITHDRAW_OTP_TTL_MS = 60 * 60 * 1000;

const walletService = new WalletService();
const bankAccountService = new BankAccountService();

const getAccountOptionLabel = (account) => {
  if (!account) return "";
  return account.type === "PAYPAL" ? account.paypalEmail : account.bankname;
};

const WalletPage = ({ userInfo, userTypeInfo, currency: currencyCode }) => {
  const location = useLocation();
  const tabFromQuery = new URLSearchParams(location.search).get("tab");
  const [activeTab, setActiveTab] = useState(0);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingBank, setLoadingBank] = useState(true);

  const { data, isLoading, error, refreshData } = useDashboardData();
  const { currencySymbol } = useCurrency(data?.walletInfo?.currency || currencyCode);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState(0);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawSendingCode, setWithdrawSendingCode] = useState(false);
  const [withdrawResendingCode, setWithdrawResendingCode] = useState(false);
  const [withdrawSecurityModalVisible, setWithdrawSecurityModalVisible] = useState(false);

  const { showErrorToast, showSuccessToast } = useToastContext();
  const token = localStorage.getItem("__extraconomy_token");
  const username = localStorage.getItem("__extraconomy_username");

  const hasBankAccounts = bankAccounts.length > 0;
  const isCustomer = userTypeInfo === "Customer";

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const res = await bankAccountService.list();
      if (!cancelled && res?.result && Array.isArray(res.result)) {
        setBankAccounts(res.result);
      } else if (!cancelled) {
        setBankAccounts([]);
      }
      if (!cancelled) setLoadingBank(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (tabFromQuery === "transacoes") setActiveTab(1);
    else if (tabFromQuery === "renda-diaria") setActiveTab(2);
    else if (tabFromQuery === "contas-bancarias") setActiveTab(3);
    else setActiveTab(0);
  }, [tabFromQuery]);

  const canRequestWithdraw =
    hasBankAccounts &&
    selectedBankAccount &&
    (withdrawAmount ?? 0) > 0 &&
    (withdrawAmount ?? 0) <= (data?.walletInfo?.balance ?? 0) &&
    !withdrawing;

  const handleAuthError = (e) => {
    if (e?.status === 401 || e?.status === 403) {
      localStorage.removeItem("__extraconomy_token");
      localStorage.removeItem("__extraconomy_username");
      window.location.replace("/login");
    }
  };

  const sendWithdrawCode = async () => {
    try {
      const res = await walletService.getWithdrawOtp(token, username);
      const ok = res.messageCode === 200000 || res.messageCode === 200001;
      if (!ok) {
        showErrorToast({ code: res.messageCode });
      }
      return ok;
    } catch (e) {
      handleAuthError(e);
      showErrorToast({ code: e?.response?.data?.messageCode || 500000 });
      return false;
    }
  };

  const handleOpenWithdrawConfirm = async () => {
    if (!canRequestWithdraw) return;
    setWithdrawSendingCode(true);
    const ok = await sendWithdrawCode();
    if (ok) setWithdrawSecurityModalVisible(true);
    setWithdrawSendingCode(false);
  };

  const handleCloseWithdrawSecurityModal = () => {
    if (!withdrawing) {
      setWithdrawSecurityModalVisible(false);
    }
  };

  const handleResendWithdrawCode = async () => {
    setWithdrawResendingCode(true);
    const ok = await sendWithdrawCode();
    setWithdrawResendingCode(false);
    return ok;
  };

  const handleWithdraw = async (code) => {
    const amount = Number(withdrawAmount) || 0;
    if (!selectedBankAccount || amount <= 0) {
      showErrorToast({ detail: "allFieldsRequired" });
      return;
    }
    const otp = (code ?? "").trim();
    if (!otp) {
      showErrorToast({ detail: "allFieldsRequired" });
      return;
    }
    setWithdrawing(true);
    try {
      const isPayPal = selectedBankAccount.type === "PAYPAL";
      const res = isPayPal
        ? await walletService.postWithdrawRequest(
            token,
            username,
            amount,
            selectedBankAccount.paypalEmail,
            otp,
          )
        : await walletService.postWithdrawRequest(
            token,
            username,
            amount,
            null,
            otp,
            typeTransfer.BANK,
            selectedBankAccount.bankname,
            selectedBankAccount.iban,
            selectedBankAccount.accountOwner,
          );
      if (res.messageCode === 500166) {
        showErrorToast({ detail: "securityCodeIsWrong" });
        setWithdrawing(false);
        return;
      }
      if (!isSuccessCode(res.messageCode)) {
        showErrorToast({ code: res.messageCode });
        setWithdrawing(false);
        return;
      }
      showSuccessToast({ detail: "amountWithdrawnSuccessfully" });
      setWithdrawSecurityModalVisible(false);
      refreshData();
    } catch (e) {
      handleAuthError(e);
      showErrorToast({ code: e?.response?.data?.messageCode || 500000 });
    } finally {
      setWithdrawing(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex justify-content-center align-items-center min-h-screen">
          <Loading classNameIcon="text-8xl" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex justify-content-center align-items-center min-h-screen">
          <p className="text-red-500">Erro ao carregar dados.</p>
        </div>
      </DashboardLayout>
    );
  }

  const walletInfo = data?.walletInfo;
  const balance = walletInfo?.balance ?? 0;
  const income = walletInfo?.income ?? 0;
  const outcome = walletInfo?.outcome ?? 0;
  const dailyIncome = walletInfo?.income ?? 0;

  const isSuccessCode = (code) => code === 200000 || code === 200001;

  return (
    <DashboardLayout>
      <nav className="flex align-items-center gap-2 mb-2 text-sm text-600" aria-label="Breadcrumb">
        <Link to="/dashboard-financeiro" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
          {localeText("financial")}
        </Link>
        <span>/</span>
        <span style={{ color: "var(--theme-text, #5C5C5C)" }}>{localeText("wallet")}</span>
      </nav>

      <div className="mb-3">
        <h2 className="text-lg font-semibold m-0" style={{ color: "var(--theme-text, #171717)" }}>
          {localeText("wallet")}
        </h2>
        <p className="text-sm m-0 mt-1" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
          {localeText("financialHubSubtitle")}
        </p>
      </div>

      <TabView
        activeIndex={activeTab}
        onTabChange={(e) => setActiveTab(e.index)}
        className="wallet-page-tabview mb-4 p-0"
      >
        <TabPanel header={localeText("overview")} className="p-0">
          {!loadingBank && !hasBankAccounts && (
            <div
              className="card border-round-md p-3 mb-4 flex align-items-center justify-content-between"
              style={{
                backgroundColor: "rgba(249, 168, 37, 0.2)",
                border: "none",
                borderLeft: "4px solid rgba(249, 168, 37, 1)",
              }}
              role="alert"
            >
              <p className="m-0" style={{ color: "var(--theme-text, #171717)" }}>
                {localeText("noBankAccountWarning")}
              </p>
              <Link
                to="/dashboard-financeiro/carteira?tab=contas-bancarias"
                className="p-button p-button-text p-button-sm underline"
                style={{ color: "var(--theme-text, #171717)" }}
                aria-label={localeText("bankAccountSettingsLink")}
              >
                {localeText("bankAccountSettingsLink")}
              </Link>
            </div>
          )}

          <div>
            <div className="grid md:flex gap-3">
              <div
                className="card border-round-xl p-4 col-12 md:flex-1"
                style={{ border: "1px solid var(--theme-border, #EBEBEB)" }}
              >
                <div className="flex align-items-center gap-2">
                  <i
                    className="pi pi-chart-pie text-lg"
                    style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                  />
                  <span
                    className="font-medium text-md"
                    style={{ color: "var(--theme-text, #171717)" }}
                  >
                    {localeText("balanceSummary")}
                  </span>
                </div>
                <hr className="my-5" style={{ borderColor: "var(--theme-border, #EBEBEB)" }} />
                <div className="flex flex-column align-items-center py-4">
                  <div
                    className="text-xs font-medium mb-2"
                    style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                  >
                    {localeText("totalAvailable").toUpperCase()}
                  </div>
                  <div
                    className="text-2xl font-medium mb-3"
                    style={{ color: "var(--theme-secondary, #1FC16B)" }}
                  >
                    {currencySymbol} {formatPrice(balance)}
                  </div>
                </div>
                <hr className="my-5" style={{ borderColor: "var(--theme-border, #EBEBEB)" }} />
                <div className="flex align-items-center">
                  <div
                    className="text-xs font-medium flex-1 text-center"
                    style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                  >
                    {localeText("credit")}: {currencySymbol} {formatPrice(income)}
                  </div>
                  <div
                    style={{
                      height: "2.5rem",
                      borderLeft: "1px solid var(--theme-border, #EBEBEB)",
                      margin: "0 1rem",
                    }}
                  />
                  <div
                    className="text-xs font-medium flex-1 text-center"
                    style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                  >
                    {localeText("debit")}: {currencySymbol} {formatPrice(outcome)}
                  </div>
                </div>
              </div>

              <div
                className="card border-round-xl p-4 col-12 md:flex-1"
                style={{ border: "1px solid var(--theme-border, #EBEBEB)" }}
              >
                <div className="flex align-items-center justify-content-between mb-3">
                  <div className="flex align-items-center gap-2">
                    <i
                      className="pi pi-arrow-right-arrow-left text-lg"
                      style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                    />
                    <span
                      className="font-medium text-md"
                      style={{ color: "var(--theme-text, #171717)" }}
                    >
                      {localeText("withdrawals")}
                    </span>
                  </div>
                  <Link to="/dashboard-withdrawintent">
                    <Button
                      label={localeText("history")}
                      className="p-button-text p-button-sm border-round-md"
                      style={{
                        border: "1px solid var(--theme-border, #EBEBEB)",
                        color: "var(--theme-text-muted, #5C5C5C)",
                      }}
                    />
                  </Link>
                </div>
                <div
                  className="card border-round-lg p-3 mb-2"
                  style={{ border: "1px solid var(--theme-border, #EBEBEB)" }}
                >
                  <div className="flex align-items-center justify-content-between gap-2">
                    <span
                      className="text-sm font-medium"
                      style={{ color: "var(--theme-text, #171717)" }}
                    >
                      {localeText("transferTo")}
                    </span>
                    <div className="flex-1">
                      <Dropdown
                        value={
                          bankAccounts.find((acc) => acc?.id === selectedBankAccount?.id) ?? null
                        }
                        options={bankAccounts}
                        onChange={(e) => setSelectedBankAccount(e.value ?? null)}
                        optionLabel={getAccountOptionLabel}
                        placeholder={localeText("selectAccount")}
                        className="p-no-outline p-inputtext-sm border-round-lg w-full"
                        disabled={!hasBankAccounts}
                      />
                    </div>
                  </div>
                  <hr className="my-3" style={{ borderColor: "var(--theme-border, #EBEBEB)" }} />
                  <div className="p-3">
                    <InputNumber
                      value={withdrawAmount}
                      onValueChange={(e) => setWithdrawAmount(e.value)}
                      min={0}
                      max={balance}
                      mode="decimal"
                      showButtons={false}
                      step={0.01}
                      placeholder={`${currencySymbol} 0,00`}
                      className="w-full"
                      inputClassName="p-0 border-none text-3xl font-medium p-no-outline text-center w-full"
                      style={{ color: "var(--theme-text, #171717)" }}
                      locale="pt-PT"
                      prefix={`${currencySymbol} `}
                      minFractionDigits={2}
                      maxFractionDigits={2}
                    />
                  </div>
                </div>
                <div className="text-sm text-600 mb-3">
                  {localeText("availableBalance")} {currencySymbol} {formatPrice(balance)}
                </div>
                <Button
                  label={
                    withdrawing || withdrawSendingCode ? (
                      <span className="flex align-items-center gap-2 justify-content-center w-full">
                        <i className="pi pi-spin pi-spinner" />
                        {localeText("processingWithdrawal")}
                      </span>
                    ) : (
                      <span className="flex align-items-center gap-2 justify-content-center w-full">
                        <i className="pi pi-arrow-right-arrow-left" />
                        {localeText("requestWithdrawalButton")}
                      </span>
                    )
                  }
                  className="w-full p-button-text border-round-md font-medium"
                  style={{
                    backgroundColor:
                      "color-mix(in srgb, var(--theme-secondary) 10%, transparent)",
                    color: "var(--theme-secondary, #335CFF)",
                  }}
                  onClick={handleOpenWithdrawConfirm}
                  disabled={!canRequestWithdraw || withdrawSendingCode}
                  loading={withdrawing || withdrawSendingCode}
                  aria-label={localeText("requestWithdrawalButton")}
                />
              </div>
            </div>
            <div className="mb-3 text-sm" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
              {localeText("originOfFunds")}
            </div>
            <div className="grid md:flex gap-3">
              <div
                className="card border-round-md p-3 flex align-items-center gap-3 col-12 md:flex-1"
                style={{ border: "1px solid var(--theme-border, #EBEBEB)" }}
              >
                <div
                  className="border-circle p-3 flex align-items-center justify-content-center"
                  style={{ border: "1px solid var(--theme-border, #EBEBEB)" }}
                >
                  <i
                    className="pi pi-dollar text-lg"
                    style={{ color: "var(--theme-text, #171717)" }}
                  />
                </div>

                <div className="flex flex-column align-items-start gap-2">
                  <div
                    className="font-medium text-sm"
                    style={{ color: "var(--theme-text, #171717)" }}
                  >
                    {localeText("transactionsCarriedOut")}
                  </div>
                  <div className="text-sm" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                    {currencySymbol} {formatPrice(balance)}
                  </div>
                </div>
              </div>

              {!isCustomer && (
                <div
                  className="card border-round-md p-3 flex align-items-center gap-3 col-12 md:flex-1"
                  style={{ border: "1px solid var(--theme-border, #EBEBEB)" }}
                >
                  <div
                    className="border-circle p-3 flex align-items-center justify-content-center"
                    style={{ border: "1px solid var(--theme-border, #EBEBEB)" }}
                  >
                    <i
                      className="pi pi-clock text-lg"
                      style={{ color: "var(--theme-text, #171717)" }}
                    />
                  </div>
                  <div className="flex flex-column align-items-start gap-2">
                    <div
                      className="font-medium text-sm"
                      style={{ color: "var(--theme-text, #171717)" }}
                    >
                      {localeText("dailyIncome")}
                    </div>
                    <div className="text-sm" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                      {currencySymbol} {formatPrice(dailyIncome)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabPanel>

        <TabPanel header={localeText("transactions")}>
          <FinancialTransactionsTable
            currencySymbol={currencySymbol}
            hasBankAccounts={hasBankAccounts}
            plan={userTypeInfo}
          />
        </TabPanel>

        <TabPanel header={localeText("dailyIncome")}>
          <LifeTimeWalletTable currencySymbol={currencySymbol} />
        </TabPanel>

        <TabPanel header={localeText("bankAccounts")}>
          <BankAccountsTab onAccountsChange={setBankAccounts} />
        </TabPanel>
      </TabView>

      <SecurityValidationModal
        visible={withdrawSecurityModalVisible}
        onHide={handleCloseWithdrawSecurityModal}
        email={userInfo?.email || data?.userInfo?.email}
        amountLabel={`${currencySymbol} ${formatPrice(withdrawAmount)}`}
        recipientName={
          selectedBankAccount?.type === "PAYPAL"
            ? selectedBankAccount?.paypalEmail
            : selectedBankAccount?.bankname
        }
        confirmTemplateKey="securityValidationConfirmWithdrawal"
        codeLength={WITHDRAW_OTP_LENGTH}
        codeTtlMs={WITHDRAW_OTP_TTL_MS}
        validating={withdrawing}
        resending={withdrawResendingCode}
        onValidate={handleWithdraw}
        onResend={handleResendWithdrawCode}
      />
    </DashboardLayout>
  );
};

export default WalletPage;
